import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { IDGOV_ERROR_CODES, ERROR_MESSAGES } from "@/lib/idgov/config";
function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
      />
    </svg>
  );
}

function getErrorTitle(errorCode: string): string {
  if (errorCode.includes("network") || errorCode.includes("server") || errorCode === "timeout") {
    return "Помилка з'єднання";
  }
  if (errorCode.includes("certificate")) {
    return "Помилка сертифіката";
  }
  if (errorCode.includes("user") || errorCode === "access_denied") {
    return "Доступ заборонено";
  }
  return "Помилка авторизації";
}

function getSuggestionByCode(errorCode: number): string {
  const adminErrors = [1, 2, 10, 12];
  const retryErrors = [3, 4, 5, 6, 7, 8, 9];
  const certificateErrors = [11, 112, 900];
  const alternativeMethodErrors = [101, 901];

  if (adminErrors.includes(errorCode)) {
    return "Зверніться до адміністратора системи.";
  }
  if (retryErrors.includes(errorCode)) {
    return "Спробуйте увійти ще раз.";
  }
  if (certificateErrors.includes(errorCode)) {
    return "Переконайтеся, що ваш сертифікат чинний та відповідає вимогам.";
  }
  if (errorCode === 100) {
    return "Ви можете спробувати авторизуватися знову.";
  }
  if (alternativeMethodErrors.includes(errorCode)) {
    return "Спробуйте інший метод авторизації.";
  }
  if (errorCode === 102) {
    return "Спробуйте пізніше або зверніться до підтримки.";
  }
  return "Спробуйте ще раз або зверніться до служби підтримки.";
}

function getSuggestionByKey(errorCode: string): string {
  if (errorCode.includes("network") || errorCode === "timeout") {
    return "Перевірте інтернет-з'єднання та спробуйте ще раз.";
  }
  if (errorCode === "server_error") {
    return "Спробуйте пізніше.";
  }
  if (errorCode.includes("certificate")) {
    return "Перевірте чинність вашого сертифіката.";
  }
  if (errorCode === "user_blocked" || errorCode === "access_denied") {
    return "Зверніться до адміністратора системи.";
  }
  return "Спробуйте увійти ще раз.";
}

interface ErrorInfo {
  title: string;
  message: string;
  suggestion: string;
}

function getErrorMessage(errorCode?: string, errorDescription?: string): ErrorInfo {
  if (errorCode) {
    const numericCode = parseInt(errorCode, 10);
    if (!isNaN(numericCode) && IDGOV_ERROR_CODES[numericCode]) {
      return {
        title: "Помилка авторизації",
        message: IDGOV_ERROR_CODES[numericCode],
        suggestion: getSuggestionByCode(numericCode),
      };
    }

    if (ERROR_MESSAGES[errorCode]) {
      return {
        title: getErrorTitle(errorCode),
        message: ERROR_MESSAGES[errorCode],
        suggestion: getSuggestionByKey(errorCode),
      };
    }
  }

  if (errorDescription) {
    return {
      title: "Помилка авторизації",
      message: errorDescription,
      suggestion: "Якщо проблема повторюється, зверніться до служби підтримки.",
    };
  }

  return {
    title: "Помилка авторизації",
    message: "Під час авторизації виникла помилка.",
    suggestion: "Спробуйте увійти ще раз. Якщо проблема повторюється, зверніться до підтримки.",
  };
}

export function AuthError() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const errorCode = searchParams.get("code") || undefined;
  const errorDescription = searchParams.get("message") || undefined;
  const { title, message, suggestion } = getErrorMessage(errorCode, errorDescription);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex items-center justify-center px-6">
        <Card className="w-full max-w-md text-center border-destructive/20">
          <CardHeader className="pb-4">
            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangleIcon className="w-10 h-10 text-destructive" />
            </div>
            <h1 className="text-2xl font-semibold">{title}</h1>
          </CardHeader>

          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground">{suggestion}</p>
            {errorCode && (
              <p className="text-xs text-muted-foreground/60 font-mono">Код: {errorCode}</p>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3 pt-2">
            <Button onClick={() => navigate("/")} className="w-full" size="lg">
              Спробувати ще раз
            </Button>
            <a
              href="mailto:support@svitlodim-dev.mindev.gov.ua?subject=Помилка авторизації Світлодім"
              className="inline-flex items-center justify-center w-full h-11 px-8 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium transition-colors"
            >
              Звернутися до підтримки
            </a>
          </CardFooter>
        </Card>
      </main>

      <footer className="py-6 text-center">
        <p className="text-xs text-muted-foreground">
          © 2026, Власність Міністерства розвитку громад та територій України
        </p>
      </footer>
    </div>
  );
}

export default AuthError;
